import { SEOHead } from "@/components/seo/SEOHead.tsx";
import { useTenant } from "@/stores/tenantStore.tsx";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button.tsx";
import { apiPost } from "@/api/client.ts";

export function ForgotPasswordPage() {
  const { siteName } = useTenant();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await apiPost("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <>
        <div className="lg:hidden mb-8 text-center">
          <span className="text-2xl font-bold text-surface-900">{siteName}</span>
        </div>
        <h1 className="text-2xl font-semibold text-surface-900 mb-2">Check your email</h1>
        <p className="text-[13px] text-surface-400 mb-6">
          If an account exists with <strong className="text-surface-700">{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
        </p>
        <Link to="/login"><Button variant="outline" fullWidth>Back to login</Button></Link>
      </>
    );
  }

  return (
    <>
      <SEOHead title="Forgot Password" description="Reset your TubeForge password." path="/forgot-password" noindex={true} />
      <div className="lg:hidden mb-8 text-center">
        <span className="text-2xl font-bold text-surface-900">{siteName}</span>
      </div>
      <h1 className="text-2xl font-semibold text-surface-900 mb-1">Forgot password</h1>
      <p className="text-[13px] text-surface-400 mb-6">Enter your email and we'll send a reset link.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        {error && <p className="text-[12px] text-danger">{error}</p>}
        <Button type="submit" loading={loading} fullWidth>Send reset link</Button>
      </form>
      <p className="mt-6 text-center text-[13px] text-surface-400">
        <Link to="/login" className="text-brand-600 hover:underline font-medium">Back to login</Link>
      </p>
    </>
  );
}
